import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  FC,
  ReactElement,
} from 'react';

import { AppContextProps, CartItem, LOCAL_STORAGE_KEY } from './models';

// ------------------
// Type Guards
// ------------------

const isValidCartItem = (item: any): item is CartItem => {
  return (
    typeof item === 'object' &&
    item !== null &&
    typeof item.code === 'number' &&
    typeof item.name === 'string' &&
    typeof item.link === 'string' &&
    typeof item.einheitKarton === 'number' &&
    typeof item.numOfItems === 'number' &&
    'image' in item
  );
};

// ------------------
// Create Context
// ------------------

const AppContext = createContext<AppContextProps | undefined>(undefined);

// ------------------
// Provider
// ------------------

export const AppProvider: FC<{ children: ReactNode }> = ({
  children,
}): ReactElement => {
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [favoriteItems, setFavoriteItems] = useState<CartItem[]>([]);
  const [orderSentIndicator, setOrderSentIndicator] = useState<boolean>(false);

  // Load from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);

        if (Array.isArray(parsed.cartItems)) {
          setCartItems(parsed.cartItems.filter(isValidCartItem));
        }

        if (Array.isArray(parsed.favoriteItems)) {
          setFavoriteItems(parsed.favoriteItems.filter(isValidCartItem));
        }
      }
    } catch (err) {
      console.error('Failed to parse localStorage data:', err);
    }

    window.addEventListener('storage', handleStorage);
    setHasLoaded(true);

    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Sync to localStorage on change
  useEffect(() => {
    if (!hasLoaded || typeof window === 'undefined') return;

    try {
      const state = JSON.stringify({ cartItems, favoriteItems });
      localStorage.setItem(LOCAL_STORAGE_KEY, state);
    } catch (err) {
      console.error('Error saving to localStorage:', err);
    }
  }, [cartItems, favoriteItems, hasLoaded]);

  // Sync across tabs
  const handleStorage = (event: StorageEvent): void => {
    if (event.key !== LOCAL_STORAGE_KEY) return;

    try {
      const parsed = event.newValue ? JSON.parse(event.newValue) : {};
      if (Array.isArray(parsed.cartItems)) {
        setCartItems(parsed.cartItems.filter(isValidCartItem));
      }
      if (Array.isArray(parsed.favoriteItems)) {
        setFavoriteItems(parsed.favoriteItems.filter(isValidCartItem));
      }
    } catch (err) {
      console.error('Error syncing storage event:', err);
    }
  };

  // ------------------
  // Actions
  // ------------------

  const addToCart = (item: CartItem): void => {
    setCartItems(prev =>
      prev.some(existing => existing.code === item.code)
        ? prev
        : [...prev, item]
    );
  };

  const removeFromCart = (code: number): void => {
    setCartItems(prev => prev.filter(item => item.code !== code));
  };

  const updateAmount = (code: number, numOfItems: number): void => {
    setCartItems(prev =>
      prev.map(item => (item.code === code ? { ...item, numOfItems } : item))
    );
  };

  const handleOrderSent = (): void => {
    setCartItems([]);
    setOrderSentIndicator(true);
  };

  const closeOrderSentDialog = (): void => {
    setOrderSentIndicator(false);
  };

  const addToFavorites = (item: CartItem): void => {
    setFavoriteItems(prev =>
      prev.some(existing => existing.code === item.code)
        ? prev
        : [...prev, item]
    );
  };

  const updateFavoriteItemStatus = (
    code: number,
    status: string | null
  ): void => {
    setFavoriteItems(prev =>
      prev.map(item => (item.code === code ? { ...item, status } : item))
    );
  };

  const removeFromFavorites = (code: number): void => {
    setFavoriteItems(prev => prev.filter(item => item.code !== code));
  };

  // ------------------
  // Context Value
  // ------------------

  const contextValue: AppContextProps = {
    cartItems,
    favoriteItems,
    orderSentIndicator,
    addToCart,
    removeFromCart,
    updateAmount,
    addToFavorites,
    updateFavoriteItemStatus,
    removeFromFavorites,
    handleOrderSent,
    closeOrderSentDialog,
  };

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

// ------------------
// Hook
// ------------------

export const useAppContext = (): AppContextProps => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
